import React, {FC, useMemo} from 'react'
import type {QHSEWorkPermitDto, WorkPermitStatus, WorkPermitCreatedByRole} from '../core/_models'

type Props = {
  visible: boolean
  record: QHSEWorkPermitDto | null
  onCancel: () => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const LABEL_SMALL = 'fw-semibold text-muted d-block mb-1'

const getStatusDisplay = (status?: WorkPermitStatus | null) => {
  if (!status) {
    return {label: '-', badgeClass: 'badge-light'}
  }
  if (status === 'SAVE') {
    return {label: 'Saved', badgeClass: 'badge-light-secondary'}
  }
  if (status === 'SUBMIT') {
    return {label: 'Submitted', badgeClass: 'badge-light-warning'}
  }
  if (status === 'CLOSE') {
    return {label: 'Closed', badgeClass: 'badge-light-success'}
  }
  return {label: String(status), badgeClass: 'badge-light'}
}

const isPermitExtended = (record: QHSEWorkPermitDto) => {
  if (!record.startDate || !record.startTime || !record.expectedEndDate || !record.expectedEndTime) return false
  const s = new Date(`${record.startDate}T${record.startTime}`)
  const e = new Date(`${record.expectedEndDate}T${record.expectedEndTime}`)
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return false
  const diffHours = (e.getTime() - s.getTime()) / (1000 * 60 * 60)
  return diffHours > 8.01
}

export const ViewWorkPermitModal: FC<Props> = ({
  visible,
  record,
  onCancel,
}) => {
  const statusMeta = useMemo(() => {
    if (!record) return {label: '-', badgeClass: 'badge-light'}
    return getStatusDisplay(record.status)
  }, [record])

  if (!visible || !record) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '75vw',
          height: '75vh',
          maxWidth: '1200px',
          maxHeight: '800px',
        }}
      >
        {/* Header */}
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <div className='d-flex flex-column'>
            <h5 className='modal-title text-dark m-6'>
              Work Permit – {record.permitName || `Permit #${record.id}`}
            </h5>
            <div className='text-muted fs-7 mx-6'>
              Vessel:&nbsp;
              <span className='fw-semibold'>
                {record.vesselName || '-'}
              </span>
              {record.startDate && (
                <>
                  <span className='mx-2'>•</span>
                  Start:&nbsp;
                  <span className='fw-semibold'>
                    {record.startDate} {record.startTime || ''}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className='d-flex align-items-center gap-3'>
            <span className={`badge ${statusMeta.badgeClass}`}>
              {statusMeta.label}
            </span>

            <button
              type='button'
              className='btn-close m-6'
              aria-label='Close'
              onClick={onCancel}
            />
          </div>
        </div>

        {/* Body with scroll */}
        <div className='flex-grow-1 overflow-auto px-4 py-3 m-6'>
          <div className='row g-4'>
            {/* Left column */}
            <div className='col-12'>
              {/* Basic Info */}
              <div className='mb-3'>
                <label className={LABEL_SMALL}>Permit Name and Form Number</label>
                <div>{record.permitName || '-'}</div>
              </div>

              <div className='row'>
                <div className='col-md-6 mb-3'>
                  <label className={LABEL_SMALL}>Office Permission Required</label>
                  <div>{record.officePermissionRequired ? 'Yes' : 'No'}</div>
                </div>
              </div>

              {/* Dates and Times */}
              <div className='row'>
                <div className='col-md-6 mb-3'>
                  <label className={LABEL_SMALL}>Start Date & Time</label>
                  <div>
                    {record.startDate} {record.startTime || ''}
                  </div>
                </div>
                {Boolean(record.expectedEndDate || record.expectedEndTime) && (
                  <div className='col-md-6 mb-3'>
                    <label className={LABEL_SMALL}>Expected End Date & Time</label>
                    <div className='d-flex align-items-center gap-2 flex-wrap'>
                      <span>
                        {record.expectedEndDate} {record.expectedEndTime || ''}
                      </span>
                      {isPermitExtended(record) && (
                        <span className='badge badge-light-primary fs-8 px-2 py-1' title='Time slot extended (> 8 hours)'>
                          + Extended
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className='row'>
                <div className='col-md-6 mb-3'>
                  <label className={LABEL_SMALL}>Actual End Date & Time</label>
                  <div>
                    {record.endDate && record.endTime ? `${record.endDate} ${record.endTime}` : '-'}
                  </div>
                </div>
              </div>

              {/* Personnel */}
              <div className='row'>
                <div className='col-md-6 mb-3'>
                  <label className={LABEL_SMALL}>Lead Person</label>
                  <div>
                    {record.leadPerson || '-'}
                    {record.leadPersonRank ? (
                      <span className='text-muted small ms-2'>({record.leadPersonRank})</span>
                    ) : null}
                  </div>
                </div>
                <div className='col-md-6 mb-3'>
                  <label className={LABEL_SMALL}>Person Responsible</label>
                  <div>
                    {record.personResponsible || '-'}
                    {record.personResponsibleRank ? (
                      <span className='text-muted small ms-2'>({record.personResponsibleRank})</span>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className='mb-3'>
                <div className='border-top pt-3 mt-3'>
                  <div className='row'>
                    <div className='col-md-6 mb-2'>
                      <label className={LABEL_SMALL}>Created By</label>
                      <div>
                        <span
                          className={`badge ${
                            record.createdByRole === 'CREW'
                              ? 'badge-light-info'
                              : 'badge-light-primary'
                          }`}
                        >
                          {record.createdByRole || '-'}
                        </span>
                      </div>
                    </div>
                    <div className='col-md-6 mb-2'>
                      <label className={LABEL_SMALL}>Submitted At</label>
                      <div>{record.submittedAt || '-'}</div>
                    </div>
                    <div className='col-md-6 mb-2'>
                      <label className={LABEL_SMALL}>Closed At</label>
                      <div>{record.closedAt || '-'}</div>
                    </div>
                    <div className='col-md-6 mb-2'>
                      <label className={LABEL_SMALL}>Office Approved</label>
                      <div>{record.officeApproved ? 'Yes' : 'No'}</div>
                    </div>
                    <div className='col-md-6 mb-2'>
                      <label className={LABEL_SMALL}>Office Approved At</label>
                      <div>{record.officeApprovedAt || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
          <button type='button' className='btn btn-light btn-sm' onClick={onCancel}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
